import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  CRASHLYTICS_OVERSEAS_ROW,
  CRASHLYTICS_ROW,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  R2_ITEMS_BOTH,
  R2_RETENTION_BOTH,
  REMEDIES,
  RETENTION_HEADERS,
  REVENUECAT_OVERSEAS_ROW,
  REVENUECAT_ROW,
  SECURITY,
  SUPPORT_ITEM_AUTO,
  SUPPORT_ITEM_INPUT,
  SUPPORT_NOTE,
  SUPPORT_PURPOSE,
  SUPPORT_RETENTION_ROW,
  changes,
  cloudflareOverseasRow,
  cloudflareRow,
  noAutomaticCollection,
  rights,
  table,
} from './common'

/**
 * 또렷 개인정보 처리방침.
 *
 * **이 방침에서 가장 조심해야 할 것은 영상이다.** 앱은 얼굴과 목소리가 든 영상을 서버로
 * 보내지만 **보관하지 않는다** — 분석이 끝나는 즉시 지운다(`server/ttoryeot` 의
 * `AnalysisService` 가 `finally` 에서 작업 폴더를 통째로 지우고, DB 에 경로조차 없다).
 * 그래서 "영상을 수집·보관한다"고 적으면 사실보다 넓고, "영상을 보내지 않는다"고 적으면
 * 거짓이다. 실제 흐름 그대로 적는다 — 그것이 제4조다.
 *
 * **이 설계가 바뀌면(영상을 저장하거나 다시 보기를 만들면) 이 방침과 스토어 데이터 보안
 * 설문을 같은 커밋에서 되돌려야 한다** (docs/privacy-policy.md).
 *
 * 수탁자 중 둘이 이 서비스에만 있다 — **Groq**(음성인식)과 **OpenRouter**(리포트·질문 생성).
 * 둘 다 국외이고, OpenRouter 는 다시 모델 제공자에게 넘긴다. 그 두 단계를 다 적는다:
 * 우리가 부르는 곳만 적으면 실제로 글이 닿는 곳을 감추는 고지가 된다.
 */

const EFFECTIVE_DATE = 'August 28, 2026'
const LAST_REVISED = 'August 28, 2026'
const CRASH_RETENTION = '90 days'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Identifying members and keeping them signed in — identifying you by a social account or by a username and password, so that what you write belongs to an account rather than to a device',
      'Storing what you write — keeping the cover letters you enter and the practice questions you keep, so they survive a change of device',
      'Generating likely interview questions — sending the cover letter you chose to a language model processor so that questions arising from it can be drafted',
      'Analysing a recorded answer — turning the video you record into a transcript, measured speech figures, and a written report (Article 4 sets out exactly what happens to the video)',
      'Counting free analyses and checking the subscription — keeping a ledger of the free analyses you have used, and asking the payment processor whether your subscription is active',
      'Keeping the service reliable — finding and fixing the places where the app errors',
      SUPPORT_PURPOSE,
    ],
    note: 'No automated decision is made that has a legal effect on you or a comparably significant impact. The report is practice feedback on one answer; it is not an assessment of you, it is never shown to anyone else, and it is not sent to any employer, recruiter, or third party.',
  },
  {
    heading: 'Article 2. Personal data processed',
    paragraphs: ['The items below are everything the Operator processes.'],
    bullets: [
      '[Required] Account identity — your username, and, if you use a social login, the account identifier and profile name that provider issues',
      '[Required] Password — only if you signed up with a username and password. **Stored as a one-way hash, so not even the Operator can read the original**',
      '[Required] Email address — used only for password reset and account verification. If you use a social login only, this follows whatever that provider supplies',
      '[Optional] Profile photo — only if you upload one yourself. You can delete it at any time',
      '[Optional] Cover letters — the company, the role, the prompts, and the answers you typed or pasted in yourself. **They are shown to no other user**',
      '[Optional] Practice questions — questions drafted from your cover letter, together with the one-line reason each was drafted, and any question you added yourself',
      '[Temporary, not retained] The video you record when you answer — including your face and voice. It is deleted the moment analysis finishes and is never stored (Article 4)',
      '[Generated automatically] Transcript of your answer — what you said, written out',
      '[Generated automatically] Measured speech figures — length, speaking time, syllables per minute, filler-word counts, pause counts, sentence counts',
      '[Generated automatically] The report — the summary, strengths, points to fix, and scores written about that one answer',
      '[Generated automatically] Analysis ledger — how many free analyses you have been granted and used, and when',
      '[Collected automatically] App diagnostics — Firebase Crashlytics installation identifier, app version and build, device model, operating system, error messages, stack traces, and crash traces',
      '[Collected automatically] Purchase record — where you subscribe, the anonymous customer identifier and store transaction record held by the payment processor',
      SUPPORT_ITEM_INPUT,
      SUPPORT_ITEM_AUTO,
    ],
    note: 'If you use a social login, the Operator never receives that provider’s password. Phone number, date of birth, gender, address, contacts, and payment card details are not requested by any route. No advertising identifier is collected, because the service carries no advertising. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected — the video in Article 4 shows your face and carries your voice, but it is neither stored nor used to identify anyone, and it is destroyed as soon as the analysis that needed it is over.',
  },
  {
    heading: 'Article 3. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      ['Account identity', 'Until you ask to withdraw (delete your account) — destroyed on request', 'Your consent'],
      ['Password (hash) and email address', 'Until you ask to withdraw — destroyed on request', 'Your consent'],
      ['Profile photo', 'Until you delete it or withdraw (destroyed immediately on withdrawal)', 'Your consent'],
      ['Cover letters and practice questions', 'Until you delete them or withdraw (destroyed immediately on withdrawal)', 'Your consent'],
      [
        'The recorded video (face and voice)',
        '**Not retained.** Deleted as soon as the analysis finishes, whether it succeeded or failed — usually within a minute of upload, and in every case within three minutes, after which the analysis is abandoned and the file deleted',
        'Your consent',
      ],
      ['Transcript, measured figures, and report', 'Until you delete that answer or withdraw (destroyed immediately on withdrawal)', 'Your consent'],
      ['Analysis ledger', 'Until you ask to withdraw — destroyed on request', 'Your consent'],
      ['App diagnostics', `${CRASH_RETENTION} from collection`, 'Your consent (processor retention cycle)'],
      ['Purchase record', 'Until the payment project is closed or deletion is requested', 'Your consent'],
      [...SUPPORT_RETENTION_ROW],
    ]),
    note: `The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute. ${SUPPORT_NOTE}`,
  },
  {
    heading: 'Article 4. What happens to the video you record (important)',
    paragraphs: [
      'To analyse an answer, the app records up to ninety seconds of video and sends it to the Operator’s server. That recording shows your face and carries your voice. **The Operator does not keep it.**',
      'The flow is this. (1) The upload is written to a temporary area in the server’s memory, not to a disk and not to any cloud storage. (2) The sound is separated out and sent to the speech-recognition processor, which returns a transcript. (3) A small number of still frames — six by default — are taken from the video and sent to the language-model processor, which returns a description of what can be seen. (4) The transcript, the measured figures, and that description are sent to the language-model processor once more, which writes the report. (5) **The whole temporary area, including the video, the extracted sound, and the still frames, is then deleted** — and it is deleted the same way if any step fails.',
      'Nothing about the video survives that sequence. There is no column in the Operator’s database that holds a video, a sound file, or an image of you; there is no address from which such a file could be fetched; and the app has no screen that plays a past recording back, because there is nothing to play. If you want to watch your own answer again, use the copy your own device kept when it recorded.',
      'What does survive is the transcript, the measured figures, and the report — all of them text. Those are yours, they are shown to no other user, and you can delete any of them from the app at any time.',
      'What the language-model processor is shown about your appearance is limited on purpose. The instruction given to it forbids any remark about appearance, attractiveness, build, age, gender, or race, and confines it to things a person can change before the next interview: whether you are looking at the camera, the angle of your head and shoulders, whether your posture drifts, whether your expression is fixed, how you are framed, and the lighting. Should that step fail or be switched off, the report is still produced from your words alone.',
    ],
    note: 'This article describes what the service actually does. Should a change be made in future to store recordings, to offer playback, or to use recordings to improve any model, this policy and the store data safety declaration will be updated together and notice given before that change goes live. The processors named in Article 6 are instructed to process what they receive only in order to answer that one request; the Operator does not consent to your data being used to train their models and has selected processors on that basis.',
  },
  {
    heading: 'Article 5. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Personal data is never sold. Your cover letters, transcripts, and reports are shown to no other user, are never sent to any employer or recruiter, and there is no sharing, comparison, or ranking between users.',
    ],
  },
  {
    heading: 'Article 6. Entrustment of personal data processing',
    paragraphs: ['To provide the service, the Operator entrusts personal data processing as follows.'],
    table: table(PROCESSOR_HEADERS, [
      ['Kakao Corp.', 'Authenticating a Kakao account sign-in', 'Account identifier and profile name', 'Limited to the moment authentication is processed'],
      ['Google LLC', 'Authenticating a Google account sign-in', 'Account identifier, name, and email address', 'Limited to the moment authentication is processed'],
      ['Apple Inc.', 'Authenticating an Apple account sign-in', 'Account identifier and email address (which you may choose to hide)', 'Limited to the moment authentication is processed'],
      [
        'Groq, Inc.',
        'Turning the sound of your answer into text',
        'The sound separated from your recording — that is, your voice and what you said. No account identifier, name, or email address is sent with it',
        'Limited to the moment the transcription is processed',
      ],
      [
        'OpenRouter, Inc. and the model provider it routes the request to',
        'Drafting likely interview questions from a cover letter, describing the still frames, and writing the report',
        'The cover letter you chose (for question drafting); the interview question, the transcript, the measured figures, the still frames taken from your video, and a short extract of the related cover letter (for the report). No account identifier, name, or email address is sent with any of them',
        'Limited to the moment the request is answered',
      ],
      [...CRASHLYTICS_ROW],
      cloudflareRow(R2_ITEMS_BOTH, R2_RETENTION_BOTH),
      [...REVENUECAT_ROW],
    ]),
    note: 'OpenRouter, Inc. is a router: it passes each request to the model provider the Operator has selected and returns the answer. That provider is therefore a further recipient of what is listed above, and the Operator names the router and the provider together rather than the router alone. The Operator selects providers that do not retain requests for training and configures the request so that it is not retained; where the selected provider changes, this policy is updated and notice given in advance. In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely.',
  },
  {
    heading: 'Article 7. Transfer of personal data abroad',
    paragraphs: [
      'Of the processors named in Article 6, all except Kakao Corp. are located outside Korea, and the entrustment transfers personal data abroad as follows. Kakao Corp. is a Korean company, so no transfer abroad is involved there.',
    ],
    table: table(OVERSEAS_HEADERS, [
      [
        'Groq, Inc.',
        'The United States',
        'The sound separated from your recording — your voice and what you said',
        'Sent over HTTPS at the moment an answer is analysed',
        'Turning the sound of your answer into text',
        'Limited to the moment the transcription is processed',
      ],
      [
        'OpenRouter, Inc. and the model provider it routes the request to',
        'The United States and, depending on the provider selected, other countries where that provider operates',
        'The cover letter you chose; the interview question, the transcript, the measured figures, the still frames from your video, and a short extract of the related cover letter',
        'Sent over HTTPS at the moment questions are drafted or an answer is analysed',
        'Drafting questions, describing the still frames, and writing the report',
        'Limited to the moment the request is answered',
      ],
      [...CRASHLYTICS_OVERSEAS_ROW],
      cloudflareOverseasRow(R2_ITEMS_BOTH, R2_RETENTION_BOTH),
      [...REVENUECAT_OVERSEAS_ROW],
      [
        'Google LLC',
        'The United States and other countries where Google operates',
        'Account identifier, name, and email address',
        'Sent over HTTPS at the moment you choose to sign in with Google',
        'Authenticating a Google account sign-in',
        'Limited to the moment authentication is processed',
      ],
      [
        'Apple Inc.',
        'The United States',
        'Account identifier and email address',
        'Sent over HTTPS at the moment you choose Sign in with Apple',
        'Authenticating an Apple account sign-in',
        'Limited to the moment authentication is processed',
      ],
    ]),
    note: OVERSEAS_REFUSAL,
  },
  DISPOSAL,
  rights(
    [
      'In the app — a cover letter, a practice question, or a past answer with its report can each be deleted directly, and your profile photo can be changed or removed there too',
      'Deleting your account — withdrawing from “My account” in the app destroys your cover letters, questions, transcripts, reports, analysis ledger, and profile photo immediately',
      'Refusing the camera or microphone permission — either can be turned off in device settings at any time. Recording an answer stops working; everything else, including cover letters and past reports, keeps working',
      'Deleting a support message — send us the reference number shown on the support page at the email address below and that message is deleted',
    ],
    'your cover letters, your practice questions, your transcripts and reports, and the text of support messages',
  ),
  noAutomaticCollection(
    'The service carries no advertising. The only purchase it offers is a monthly subscription that raises the number of answers you may have analysed each month; payment is carried out by the Apple App Store or Google Play, so the Operator never sees your card details, and a subscription is cancelled in the store’s own subscription settings, which the app can open for you but cannot act on itself. The Operator processes only the purchase record described in Articles 2, 3, 6, and 7.',
  ),
  CHILDREN,
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(EFFECTIVE_DATE, [
    'August 28, 2026 — established (structured on Article 30 of the Personal Information Protection Act, including notice of transfers abroad)',
  ]),
]

export const TTORYEOT_POLICY: PolicyDocument = {
  slug: 'ttoryeot',
  title: '또렷 (Ttoryeot) Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for 또렷 (Ttoryeot, “the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
